import { createContext, useContext, useCallback, ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./use-auth";

// ── Types ──────────────────────────────────────────────────

export type ActivationStatus = "shortlisted" | "coordination" | "confirmed" | "waiting-feedback" | "completed" | "cancelled";

export type ActivationFormat =
  | "sponsorship"
  | "private-experience"
  | "retreat-residency"
  | "workshop"
  | "product-limited-edition"
  | "gifting"
  | "testing"
  | "panel"
  | "branded-content"
  | "ugc"
  | "community-sponsorship"
  | "ambassador"
  | "affiliate"
  | "co-created-event"
  | "other";

export const FORMAT_LABELS: Record<ActivationFormat, string> = {
  sponsorship: "Event Sponsorship",
  "private-experience": "Private Experience Invite",
  "retreat-residency": "Retreat & Residency",
  workshop: "Workshop",
  "product-limited-edition": "Product Limited Edition",
  gifting: "Product Seeding",
  testing: "Product Testing",
  panel: "Panel",
  "branded-content": "Branded Content",
  ugc: "UGC",
  "community-sponsorship": "Community Sponsorship",
  ambassador: "Ambassador Program",
  affiliate: "Affiliate Program",
  "co-created-event": "Co-created Event",
  other: "Other",
};

export type CostType = "cash" | "product-value" | "mixed";

export interface ActivationMessage {
  id: string;
  from: "brand" | "community";
  senderName: string;
  content: string;
  date: string;
}

export interface ActivationCondition {
  label: string;
  value: string;
  agreed: boolean;
}

export interface ActivationTimeline {
  label: string;
  date: string;
  completed: boolean;
}

export type ActivityLogType = "email" | "call" | "meeting" | "whatsapp" | "other";

export interface ActivityLogEntry {
  id: string;
  type: ActivityLogType;
  date: string;
  note: string;
}

export interface ActivationDocument {
  id: string;
  type: "file" | "url";
  name: string;
  url?: string;
  dateAdded: string;
}

export interface ActivationResults {
  gir: number;
  cpgi: number;
  womcPosts: number;
  womc: number;
  womcReach: number;
  hasTracking: boolean;
  trackableConversions?: number;
  totalCost: number;
  shortTermRevenue: number;
  estimatedLTV: number;
  roi: number;
  verbatims: { quote: string; initial: string }[];
}

export interface ActivationCompensation {
  cashBudget?: number;
  budget?: number;
  nbGuests?: number;
  productName?: string;
  productQuantity?: number;
  productUnitValue?: number;
  productUrl?: string;
  nbSamples?: number;
  sampleValue?: number;
  monthlyCompensation?: number;
  durationMonths?: number;
  commissionPercent?: number;
  fixedFee?: number;
  resources?: string;
}

export function calcValueOfExchange(comp: ActivationCompensation, format: ActivationFormat): number {
  const { cash, product } = calcCompensationSplit(comp, format);
  return cash + product;
}

export function calcCompensationSplit(comp: ActivationCompensation, format: ActivationFormat): { cash: number; product: number } {
  const cash = comp.cashBudget || 0;
  switch (format) {
    case "gifting":
    case "product-limited-edition":
      return { cash, product: (comp.productQuantity || 0) * (comp.productUnitValue || 0) };
    case "testing": {
      // The product flow stores units x retail value; older rows used samples x sample value.
      const fromProduct = (comp.productQuantity || 0) * (comp.productUnitValue || 0);
      return { cash, product: fromProduct || (comp.nbSamples || 0) * (comp.sampleValue || 0) };
    }
    case "ambassador":
      return { cash, product: (comp.monthlyCompensation || 0) * (comp.durationMonths || 0) };
    case "affiliate":
      return { cash, product: comp.fixedFee || 0 };
    default:
      return { cash: cash || (comp.budget || 0), product: 0 };
  }
}

export function getCompensationLabel(comp: ActivationCompensation, format: ActivationFormat): string {
  const { cash, product } = calcCompensationSplit(comp, format);
  const isCashOnly = cash > 0 && product === 0;
  const isProductOnly = cash === 0 && product > 0;
  if (isCashOnly) return "cash";
  if (isProductOnly) return "product value";
  if (cash > 0 && product > 0) return "cash + product";
  return "";
}

export interface EngageActivation {
  id: string;
  communityId: string;
  communityName: string;
  communityImage: string;
  communityCity: string;
  communityTheme: string;
  status: ActivationStatus;
  format: ActivationFormat;
  budget: number;
  date: string;
  goals?: string;
  contactName?: string;
  contactRole?: string;
  contactEmail?: string;
  notes?: string;
  activityLog?: ActivityLogEntry[];
  documents?: ActivationDocument[];
  sharedDocuments?: ActivationDocument[];
  messages?: ActivationMessage[];
  conditions?: ActivationCondition[];
  timeline?: ActivationTimeline[];
  results?: ActivationResults;
  assignee?: string;
  proposedDate?: string;
  roughTimeline?: string;
  compensation?: ActivationCompensation;
  costType?: CostType;
  customFormatDescription?: string;
  internalBudget?: number;
  marginNotes?: string;
  /** Family-specific extras stored as jsonb on the activation row. */
  details?: Record<string, unknown>;
  useCustomMessage?: boolean;
  customMessage?: string;
}

export type CampaignStatus = "planning" | "active" | "completed" | "cancelled";

export interface EngageCampaign {
  id: string;
  name: string;
  description: string;
  objective?: string;
  status: CampaignStatus;
  targetDate: string;
  createdBy: string;
  activations: EngageActivation[];
}

/** An activation recommended when a campaign was planned: kept on the campaign until communities are matched to it. */
export interface PlannedActivation {
  format: ActivationFormat;
  why?: string;
}

export interface NewActivationsInput {
  campaignId: string;
  communityIds: string[];
  format: ActivationFormat;
  status?: ActivationStatus;
  date?: string;
  goals?: string;
  duration?: string;
  contactName?: string;
  contactEmail?: string;
  valueOfExchange?: number;
  compensation?: ActivationCompensation;
  /** Family-specific extras stored in activations.details. */
  details?: Record<string, unknown>;
}

// ── Context ────────────────────────────────────────────────

interface EngageCampaignsContextType {
  campaigns: EngageCampaign[];
  isLoading: boolean;
  createCampaign: (data: { name: string; description: string; targetDate: string; objective?: string; plannedActivations?: PlannedActivation[] }) => Promise<EngageCampaign>;
  /** The activations recommended at planning time, whether or not communities have been matched yet. */
  getPlannedActivations: (campaignId: string) => PlannedActivation[];
  getCampaign: (id: string) => EngageCampaign | undefined;
  deleteCampaign: (id: string) => Promise<void>;
  addCommunitiesToCampaign: (campaignId: string, communityIds: string[], format?: ActivationFormat) => void;
  removeActivationByCommunity: (campaignId: string, communityId: string) => void;
  isCommunityInCampaign: (campaignId: string, communityId: string) => boolean;
  findActivation: (activationId: string) => { activation: EngageActivation; campaignId: string } | null;
  createActivations: (input: NewActivationsInput) => Promise<void>;
  updateActivation: (activationId: string, updates: Partial<EngageActivation>) => void;
  updateCampaign: (campaignId: string, updates: Partial<Omit<EngageCampaign, "id" | "activations">>) => void;
  /** Moves existing activations into a campaign. An activation lives in exactly one campaign. */
  moveActivations: (campaignId: string, activationIds: string[]) => Promise<void>;
}

const EngageCampaignsContext = createContext<EngageCampaignsContextType>({
  campaigns: [],
  isLoading: false,
  createCampaign: async () => ({} as EngageCampaign),
  getPlannedActivations: () => [],
  getCampaign: () => undefined,
  deleteCampaign: async () => {},
  addCommunitiesToCampaign: () => {},
  removeActivationByCommunity: () => {},
  isCommunityInCampaign: () => false,
  findActivation: () => null,
  createActivations: async () => {},
  updateActivation: () => {},
  updateCampaign: () => {},
  moveActivations: async () => {},
});

// ── Helper to map DB row to EngageActivation ───────────────

function mapDbActivation(row: any): EngageActivation {
  const community = row.communities;
  return {
    id: row.id,
    communityId: row.community_id,
    communityName: community?.name || "Unknown",
    communityImage: community?.cover_image_url || community?.image_url || "",
    communityCity: community?.location_city || "",
    communityTheme: community?.theme || "",
    status: (row.status || "shortlisted") as ActivationStatus,
    format: (row.activation_type || "sponsorship") as ActivationFormat,
    budget: row.cash_budget || row.value_of_exchange || 0,
    date: row.preferred_date || "",
    goals: row.activation_notes || "",
    contactName: row.contact_name || "",
    contactEmail: row.contact_email || "",
    notes: row.internal_notes || "",
    assignee: row.assignee_id || undefined,
    details: (row.details && typeof row.details === "object" ? row.details : {}) as Record<string, unknown>,
    compensation: {
      cashBudget: row.cash_budget || 0,
      productName: row.product_name || undefined,
      productQuantity: row.product_quantity || undefined,
      productUnitValue: row.product_unit_value || undefined,
      productUrl: row.product_url || undefined,
      monthlyCompensation: row.monthly_compensation || undefined,
      durationMonths: parseInt(row.duration, 10) || undefined,
      commissionPercent: row.commission_pct || undefined,
      fixedFee: row.commission_fixed || undefined,
      resources: row.resources_description || undefined,
      nbGuests: row.nb_guests || undefined,
    },
  };
}

function mapDbCampaign(row: any): EngageCampaign {
  const activations = (row.activations || []).map(mapDbActivation);
  return {
    id: row.id,
    name: row.name,
    description: row.description || "",
    objective: row.objective || undefined,
    status: (row.status || "planning") as CampaignStatus,
    targetDate: row.target_date || "",
    createdBy: row.created_by || "",
    activations,
  };
}

// ── Provider ───────────────────────────────────────────────

export const EngageCampaignsProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch user's brand_id
  const { data: profile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("brand_id")
        .eq("id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const brandId = profile?.brand_id;

  // Fetch campaigns with activations and community info (no profile joins)
  const { data: campaigns = [], isLoading } = useQuery({
    queryKey: ["campaigns", brandId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("campaigns")
        .select("id, name, status, description, objective, target_date, total_budget, created_by, brand_id, activations(*, communities(id, name, location_city, cover_image_url, image_url, active_members, audience_size, network_density, theme))")
        .eq("brand_id", brandId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []).map(mapDbCampaign);
    },
    enabled: !!brandId,
  });

  // planned_activations is newer than the generated types, and a database without it yet simply has none.
  const { data: plannedByCampaign = {} } = useQuery({
    queryKey: ["campaign-planned", brandId],
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).from("campaigns").select("id, planned_activations").eq("brand_id", brandId!);
      if (error) return {} as Record<string, PlannedActivation[]>;
      const map: Record<string, PlannedActivation[]> = {};
      (data || []).forEach((r: { id: string; planned_activations: unknown }) => {
        if (Array.isArray(r.planned_activations)) map[r.id] = r.planned_activations as PlannedActivation[];
      });
      return map;
    },
    enabled: !!brandId,
  });
  const getPlannedActivations = useCallback((campaignId: string) => plannedByCampaign[campaignId] ?? [], [plannedByCampaign]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["campaigns", brandId] });
    queryClient.invalidateQueries({ queryKey: ["campaign-planned", brandId] });
  };

  // Create campaign
  const createMutation = useMutation({
    mutationFn: async (data: { name: string; description: string; targetDate: string; objective?: string; plannedActivations?: PlannedActivation[] }) => {
      if (!user || !brandId) throw new Error("Not authenticated");
      const { data: row, error } = await supabase
        .from("campaigns")
        .insert({
          name: data.name,
          description: data.description || null,
          objective: data.objective || null,
          target_date: data.targetDate || null,
          status: "planning",
          brand_id: brandId,
          created_by: user.id,
        })
        .select("*, activations(*, communities(id, name, location_city, cover_image_url, image_url))")
        .single();
      if (error) throw error;
      if (data.plannedActivations?.length) {
        // Saved apart from the insert, so a database without the column still creates the campaign.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (supabase as any).from("campaigns").update({ planned_activations: data.plannedActivations }).eq("id", row.id);
      }
      return mapDbCampaign(row);
    },
    onSuccess: (created, variables) => {
      // Put the new campaign (and its recommended activations) in the cache before the refetch, so its page opens
      // straight away instead of flashing "not found" while the list reloads.
      queryClient.setQueryData<EngageCampaign[]>(["campaigns", brandId], (old = []) => [created, ...old.filter((c) => c.id !== created.id)]);
      if (variables.plannedActivations?.length) {
        queryClient.setQueryData<Record<string, PlannedActivation[]>>(["campaign-planned", brandId], (old = {}) => ({ ...old, [created.id]: variables.plannedActivations! }));
      }
      invalidate();
    },
  });

  // Update campaign
  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Record<string, any> }) => {
      const dbUpdates: Record<string, any> = {};
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.description !== undefined) dbUpdates.description = updates.description;
      if (updates.objective !== undefined) dbUpdates.objective = updates.objective;
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.targetDate !== undefined) dbUpdates.target_date = updates.targetDate;
      const { error } = await supabase.from("campaigns").update(dbUpdates as any).eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  // Delete campaign (activations cascade or delete manually)
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      // Get activation ids for this campaign
      const { data: activations } = await supabase
        .from("activations")
        .select("id")
        .eq("campaign_id", id);
      const activationIds = (activations || []).map((a: any) => a.id);

      if (activationIds.length > 0) {
        // 1) activity_log
        await supabase.from("activity_log").delete().in("activation_id", activationIds);
        // 2) activation_documents
        await supabase.from("activation_documents").delete().in("activation_id", activationIds);
        // 3) activation_conditions
        await supabase.from("activation_conditions").delete().in("activation_id", activationIds);
        // 4) activations
        await supabase.from("activations").delete().eq("campaign_id", id);
      }
      // 5) campaign_members
      await supabase.from("campaign_members").delete().eq("campaign_id", id);
      // 6) campaign
      const { error } = await supabase.from("campaigns").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  // Add communities as activations
  const addCommunitiesMutation = useMutation({
    mutationFn: async ({ campaignId, communityIds, format }: { campaignId: string; communityIds: string[]; format?: ActivationFormat }) => {
      const rows = communityIds.map((cId) => ({
        campaign_id: campaignId,
        community_id: cId,
        status: "shortlisted",
        activation_type: format || "sponsorship",
      }));
      const { error } = await supabase.from("activations").insert(rows);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  // Create activations with a given format and terms (used by the family workspaces)
  const createActivationsMutation = useMutation({
    mutationFn: async (input: NewActivationsInput) => {
      const c = input.compensation || {};
      const rows = input.communityIds.map((cId) => ({
        campaign_id: input.campaignId,
        community_id: cId,
        status: input.status || "shortlisted",
        activation_type: input.format,
        preferred_date: input.date || null,
        activation_notes: input.goals || null,
        duration: input.duration || null,
        contact_name: input.contactName || null,
        contact_email: input.contactEmail || null,
        value_of_exchange: input.valueOfExchange ?? null,
        cash_budget: c.cashBudget ?? null,
        product_name: c.productName ?? null,
        product_quantity: c.productQuantity ?? null,
        product_unit_value: c.productUnitValue ?? null,
        product_url: c.productUrl ?? null,
        monthly_compensation: c.monthlyCompensation ?? null,
        commission_pct: c.commissionPercent ?? null,
        commission_fixed: c.fixedFee ?? null,
        resources_description: c.resources ?? null,
        nb_guests: c.nbGuests ?? null,
        ...(input.details ? { details: input.details } : {}),
      }));
      const { error } = await supabase.from("activations").insert(rows as any);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  // Remove activation by community
  const removeActivationMutation = useMutation({
    mutationFn: async ({ campaignId, communityId }: { campaignId: string; communityId: string }) => {
      const { error } = await supabase
        .from("activations")
        .delete()
        .eq("campaign_id", campaignId)
        .eq("community_id", communityId);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  // Auto-compute campaign status from activations
  const computeAndSyncCampaignStatus = async (campaignId: string) => {
    const { data: activationRows } = await supabase
      .from("activations")
      .select("status")
      .eq("campaign_id", campaignId);
    const statuses = (activationRows || []).map((a: any) => a.status).filter((s: string) => s !== "cancelled");
    let computed: CampaignStatus = "planning";
    if (statuses.length > 0) {
      if (statuses.every((s: string) => s === "completed")) computed = "completed";
      else if (statuses.some((s: string) => ["coordination", "confirmed", "waiting-feedback"].includes(s))) computed = "active";
    }
    await supabase.from("campaigns").update({ status: computed }).eq("id", campaignId);
  };

  // Update activation
  const updateActivationMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Record<string, any> }) => {
      const dbUpdates: Record<string, any> = {};
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.format !== undefined) dbUpdates.activation_type = updates.format;
      if (updates.goals !== undefined) dbUpdates.activation_notes = updates.goals;
      if (updates.contactName !== undefined) dbUpdates.contact_name = updates.contactName;
      if (updates.contactEmail !== undefined) dbUpdates.contact_email = updates.contactEmail;
      if (updates.notes !== undefined) dbUpdates.internal_notes = updates.notes;
      if (updates.date !== undefined) dbUpdates.preferred_date = updates.date;
      if (updates.compensation) {
        const c = updates.compensation;
        if (c.cashBudget !== undefined) dbUpdates.cash_budget = c.cashBudget;
        if (c.productName !== undefined) dbUpdates.product_name = c.productName;
        if (c.productQuantity !== undefined) dbUpdates.product_quantity = c.productQuantity;
        if (c.productUnitValue !== undefined) dbUpdates.product_unit_value = c.productUnitValue;
        if (c.productUrl !== undefined) dbUpdates.product_url = c.productUrl;
        if (c.monthlyCompensation !== undefined) dbUpdates.monthly_compensation = c.monthlyCompensation;
        if (c.commissionPercent !== undefined) dbUpdates.commission_pct = c.commissionPercent;
        if (c.fixedFee !== undefined) dbUpdates.commission_fixed = c.fixedFee;
        if (c.resources !== undefined) dbUpdates.resources_description = c.resources;
        if (c.nbGuests !== undefined) dbUpdates.nb_guests = c.nbGuests;
      }
      if (Object.keys(dbUpdates).length > 0) {
        const { error } = await supabase.from("activations").update(dbUpdates as any).eq("id", id);
        if (error) throw error;
      }
      // If status changed, recompute campaign status
      if (updates.status !== undefined) {
        const found = campaigns.find((c) => c.activations.some((a) => a.id === id));
        if (found) await computeAndSyncCampaignStatus(found.id);
      }
    },
    onSuccess: invalidate,
  });

  // Move existing activations into another campaign, then refresh the status of every campaign that changed
  const moveActivationsMutation = useMutation({
    mutationFn: async ({ campaignId, activationIds }: { campaignId: string; activationIds: string[] }) => {
      if (activationIds.length === 0) return;
      const sources = campaigns
        .filter((c) => c.id !== campaignId && c.activations.some((a) => activationIds.includes(a.id)))
        .map((c) => c.id);
      const { error } = await supabase.from("activations").update({ campaign_id: campaignId }).in("id", activationIds);
      if (error) throw error;
      await Promise.all([campaignId, ...sources].map(computeAndSyncCampaignStatus));
    },
    onSuccess: invalidate,
  });

  const getCampaign = useCallback((id: string) => campaigns.find((c) => c.id === id), [campaigns]);

  const isCommunityInCampaign = useCallback(
    (campaignId: string, communityId: string) => {
      const camp = campaigns.find((c) => c.id === campaignId);
      return camp?.activations.some((a) => a.communityId === communityId) ?? false;
    },
    [campaigns]
  );

  const findActivation = useCallback(
    (activationId: string): { activation: EngageActivation; campaignId: string } | null => {
      for (const campaign of campaigns) {
        const activation = campaign.activations.find((a) => a.id === activationId);
        if (activation) return { activation, campaignId: campaign.id };
      }
      return null;
    },
    [campaigns]
  );

  const contextValue: EngageCampaignsContextType = {
    campaigns,
    isLoading,
    createCampaign: async (data) => createMutation.mutateAsync(data),
    getPlannedActivations,
    getCampaign,
    deleteCampaign: async (id) => deleteMutation.mutateAsync(id),
    addCommunitiesToCampaign: (campaignId, communityIds, format) =>
      addCommunitiesMutation.mutate({ campaignId, communityIds, format }),
    removeActivationByCommunity: (campaignId, communityId) =>
      removeActivationMutation.mutate({ campaignId, communityId }),
    isCommunityInCampaign,
    findActivation,
    createActivations: async (input) => createActivationsMutation.mutateAsync(input),
    updateActivation: (activationId, updates) =>
      updateActivationMutation.mutate({ id: activationId, updates }),
    updateCampaign: (campaignId, updates) =>
      updateMutation.mutate({ id: campaignId, updates }),
    moveActivations: async (campaignId, activationIds) =>
      moveActivationsMutation.mutateAsync({ campaignId, activationIds }),
  };

  return (
    <EngageCampaignsContext.Provider value={contextValue}>
      {children}
    </EngageCampaignsContext.Provider>
  );
};

export const useEngageCampaigns = () => useContext(EngageCampaignsContext);
